import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  ButtonProps,
  FormHelperText,
  IconButton,
} from '@mui/material';

interface PrimaryActionButtonProps extends ButtonProps {
  width?: string;
}

export const PrimaryActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'width',
})<PrimaryActionButtonProps>(({ width }) => ({
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: '500',
  marginBottom: '16px',
  width: width || 'auto',
  backgroundColor: 'var(--primary-blue)',
  color: 'var(--bg-color)',
  borderRadius: '8px',
  border: '1px solid var(--border-blue)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'var(--hover-blue)',
  },
}));

export const ButtonIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--bg-color)',
});

interface FilterButtonProps extends ButtonProps {
  width?: string;
  marginBottom?: string;
}

export const FilterButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'width' && prop !== 'marginBottom' && prop !== 'ml',
})<FilterButtonProps & { ml?: any }>(
  ({ width, marginBottom = '16px', ml }) => ({
    height: '40px',
    fontFamily: 'Outfit',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: marginBottom,
    marginLeft:
      ml !== undefined ? (typeof ml === 'number' ? `${ml * 8}px` : ml) : '16px',
    width: width || 'auto',
    backgroundColor: 'var(--text-bg)',
    color: 'var(--primary-brand-blue)',
    border: '1px solid var(--primary-brand-blue)',
    borderRadius: '8px',
    textTransform: 'none',
  })
);

export const ButtonStyled = styled(Button)<ButtonProps>((props) => ({
  backgroundColor: 'var(--primary-blue)',
  color: 'var(--bg-color)',
  borderRadius: '8px',
  border: '1px solid var(--border-blue)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'var(--hover-blue)',
  },
  props,
}));

export const FilterIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--primary-brand-blue)',
});

export const OverflowContainer = styled(Box)({
  overflowX: 'auto',
  width: 'calc(100% - 300px)',
  '@media (max-width: 900px)': {
    width: '100%',
  },
});

export const FlexOverflowContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'auto',
  width: 'calc(100% - 300px)',
  '@media (max-width: 900px)': {
    width: '100%',
  },
});

export const ScrollableBox = styled(Box)({
  flexGrow: 1,
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
});

export const DividerLine = styled(Box)({
  height: '1px',
  backgroundColor: 'var(--bg-color)',
  width: '90%',
  opacity: 0.5,
  marginBottom: '8px',
});

export const FilterPopoverIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--primary-blue)',
  backgroundColor: 'var(--hover-white)',
  borderRadius: '20px',
  padding: '4px',
  marginRight: '16px',
});

export const FilterPopoverTitle = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '500',
  fontSize: '16px',
  color: 'var(--primary-brand-blue)',
});

export const FilterPopoverDivider = styled(Box)({
  borderBottom: '2px solid var(--hover-white)',
  marginTop: '16px',
  marginBottom: '16px',
});

export const FilterCloseIcon = styled(Box)({
  position: 'absolute',
  top: 16,
  right: 16,
  fontSize: '20px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  '&:hover': {
    color: 'var(--error)',
  },
});

export const BaseLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  display: 'block',
});

export const FilterLabel = styled(BaseLabel, {
  shouldForwardProp: (prop) => prop !== 'marginBottom',
})<{ marginBottom?: string }>(({ marginBottom }) => ({
  fontSize: '12px',
  color: 'var(--primary-navy)',
  marginBottom: marginBottom || 0,
}));

export const FieldLabel = styled(BaseLabel)({
  fontSize: '16px',
  marginBottom: '16px',
});

export const DateLabel = styled(BaseLabel)({
  fontSize: '16px',
  color: 'var(--subtext)',
  marginBottom: '12px',
});

export const DropdownIcon = styled(Box)({
  position: 'absolute',
  top: '50%',
  right: '12px',
  transform: 'translateY(-50%)',
  fontSize: '16px',
  color: 'var(--instructions)',
  '&:hover': { cursor: 'pointer' },
});

interface SmallActionButtonProps {
  btnVariant?: 'primary' | 'inactive' | 'secondary';
  height?: string;
  width?: string;
  mr?: number;
  props?: ButtonProps;
}

export const SmallActionButton = styled(Button)<SmallActionButtonProps>`
  height: ${(props) => props.height || '28px'};
  width: ${(props) => props.width || '70px'};
  font-size: 14px;
  font-weight: 500;
  margin-right: ${(props) => `${props.mr || 0}px`};
  text-transform: none;
  border-radius: 8px;

  ${(props) => {
    const variant = props.btnVariant || 'primary';
    switch (variant as 'primary' | 'inactive' | 'secondary') {
      case 'primary':
        return `
          background-color: var(--primary-blue);
          color: var(--bg-color);
          border: 1px solid var(--border-blue);
          &:hover {
            background-color: var(--hover-blue);
          }
        `;
      case 'inactive':
        return `
          background-color: var(--inactive-btn);
          color: var(--instructions);
          border: 1px solid var(--inactive-border);
        `;
      case 'secondary':
        return `
          background-color: var(--inactive-btn);
          color: var(--primary-blue);
          &:hover {
            border: 1px solid var(--border-blue);
          }
        `;
      default:
        return `
          background-color: var(--primary-blue);
          color: var(--bg-color);
          border: 1px solid var(--border-blue);
        `;
    }
  }}
`;

export const AddItemIcon = styled(Box)({
  fontSize: '24px',
  color: 'var(--primary-blue)',
  marginRight: '4px',
});

export const AddItemText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-blue)',
});

export const SpacedContainer = styled(Box)({
  marginTop: '8px',
});

export const FlexSpaceBetweenDateContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

export const FlexStartContainer = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
});

export const JustifyStartContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
});

interface ClickableContainerProps {
  width?: string;
  mt?: number;
  mb?: number;
}

export const ClickableContainer = styled(Box, {
  shouldForwardProp: (prop) => !['width', 'mt', 'mb'].includes(prop as string),
})<ClickableContainerProps>(({ width, mt, mb }) => ({
  cursor: 'pointer',
  width: width || 'auto',
  display: 'flex',
  marginTop: mt ? `${mt * 8}px` : 0,
  marginBottom: mb ? `${mb * 8}px` : 0,
}));

export const RemoveItemIcon = styled(Box)({
  fontSize: '18px',
  color: 'var(--instructions)',
  cursor: 'pointer',
});

interface StyledTextProps {
  fontSize?: string;
  fontWeight?: number | string;
  color?: string;
  marginRight?: string;
  marginBottom?: string;
  marginTop?: string;
  marginLeft?: string;
  display?: string;
}

export const StyledText = styled(Typography, {
  shouldForwardProp: (prop) =>
    ![
      'fontSize',
      'fontWeight',
      'color',
      'marginRight',
      'marginBottom',
      'marginTop',
      'marginLeft',
      'display',
    ].includes(prop as string),
})<StyledTextProps>(
  ({
    fontSize,
    fontWeight,
    color,
    marginRight,
    marginBottom,
    marginTop,
    marginLeft,
    display,
  }) => ({
    fontFamily: 'Outfit',
    fontSize: fontSize || '16px',
    fontWeight: fontWeight || 400,
    color: color || 'var(--primary-navy)',
    marginRight: marginRight || '0px',
    marginBottom: marginBottom || '0px',
    marginTop: marginTop || '0px',
    marginLeft: marginLeft || '0px',
    display: display || 'block',
  })
);

interface ErrorHelperTextProps {
  marginTop?: string;
}

export const ErrorHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'marginTop',
})<ErrorHelperTextProps>(({ marginTop }) => ({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 0,
  marginTop: marginTop || '2px',
  position: 'absolute',
}));

export const ErrorHelperTextContainer = styled(FormHelperText)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '10px',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 0,
  marginTop: '-10px',
  position: 'absolute',
  borderRadius: '4px',
  padding: '4px 8px',
  backgroundColor: 'var(--error-snackbar)',
  color: 'var(--error)',
});

export const FlexContainer = styled(Box)({
  display: 'flex',
});

export const FlexCenterContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const FlexCenterHorizontalContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

export const FlexSpaceBetweenContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '24px',
});

interface FlexColumnContainerProps {
  mt?: number;
}

export const FlexColumnContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mt',
})<FlexColumnContainerProps>(({ mt }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: mt ? `${mt * 8}px` : 0,
}));

interface FlexGapContainerProps {
  mt?: number;
  mb?: number;
}

export const FlexGapContainer = styled(Box, {
  shouldForwardProp: (prop) => !['mt', 'mb'].includes(prop as string),
})<FlexGapContainerProps>(({ mt, mb }) => ({
  display: 'flex',
  gap: '16px',
  marginTop: mt ? `${mt * 8}px` : 0,
  marginBottom: mb ? `${mb * 8}px` : 0,
}));

interface FlexItemProps {
  mt?: number;
}

export const FlexItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mt',
})<FlexItemProps>(({ mt }) => ({
  flex: 1,
  marginTop: mt ? `${mt * 8}px` : 0,
}));

export const SidebarBackdrop = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 1150,
  transition: 'opacity 0.3s ease',
});

export const MobileMenuButton = styled(IconButton)({
  position: 'fixed',
  top: '20px',
  left: '20px',
  zIndex: 1100,
  backgroundColor: 'var(--white)',
  boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
  '&:hover': {
    backgroundColor: 'var(--hover-white)',
  },
});

export const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  flexGrow: 1,
  paddingTop: '24px',
  paddingLeft: isMobile ? '16px' : '32px',
  paddingRight: isMobile ? '16px' : '32px',
  marginLeft: isMobile ? 0 : '300px',
  display: 'flex',
  flexDirection: 'column',
  width: isMobile ? '100%' : 'calc(100% - 300px)',
  transition: 'margin-left 0.3s ease, width 0.3s ease',
}));

export const HeaderContainer = styled(Box)({
  borderBottom: '1px solid',
  borderColor: 'var(--border)',
  marginBottom: '16px',
  width: '100%',
});

export const ActionToolbar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: isMobile ? 'flex-start' : 'flex-end',
  alignItems: isMobile ? 'stretch' : 'flex-start',
  marginBottom: '8px',
  gap: isMobile ? '8px' : 0,
}));

export const SearchContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  display: 'flex',
  marginRight: isMobile ? 0 : 'auto',
  flexDirection: isMobile ? 'column' : 'row',
  gap: isMobile ? '8px' : 0,
}));

export const ButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  alignSelf: 'flex-start',
  marginTop: isMobile ? '8px' : 0,
}));

export const ChildrenContainer = styled(Box)({
  overflowX: 'auto',
  width: '100%',
});
